<template>
  <div class="ins-select-box">
    <el-select
      :model-value="values[0]"
      :placeholder="placeholder"
      clearable
      filterable
      autocomplete="nony"
      @change="onCateChange"
      @keyup.enter="onKeyupEnter"
    >
      <el-option
        v-for="(v) in categories"
        :key="v.id"
        :value="v.id"
        :label="v.name"
      />
    </el-select>
    <el-select
      :model-value="values[1]"
      :disabled="isNull(values[0])"
      class="asb-ins"
      :placeholder="placeholder"
      clearable
      filterable
      autocomplete="nony"
      @change="onSubCateChange"
      @keyup.enter="onKeyupEnter"
    >
      <el-option
        v-for="(v) in subCategories"
        :key="v.id"
        :value="v.id"
        :label="v.name"
      />
    </el-select>
    <el-select
      v-if="enableThird"
      class="asb-ins-third"
      :style="`visibility: ${noThirdOpts ? 'hidden' : 'visible'}`"
      :model-value="values[2]"
      :disabled="isNull(values[1]) || noThirdOpts"
      clearable
      :placeholder="placeholder"
      @change="onThirdCateChange"
    >
      <el-option
        v-for="(v) in thirdCategories"
        :key="v.id"
        :value="v.id"
        :label="v.name"
      />
    </el-select>
  </div>
</template>

<script setup lang="ts">
import { PropType, ref, watch } from 'vue';
import { isNull } from '@core/tools';
import { ElMessage } from 'element-plus';
import { queryIndustryTree } from '@core/api';

defineOptions({ name: 'CurrentAreaSelect' });

interface IBaseOpt {
  name: string;
  id: number;
  mccCode?: string;
  children?: IBaseOpt[];
}

const emit = defineEmits<{
  'key-enter': [];
  'get-mcc': [value: string];
  'without-third-category': [value: boolean];
  change: [value: number[], withoutTown?: boolean];
}>();

const props = defineProps({
  initialCodes: {
    type: Array as PropType<number[]>,
    default() {
      return [];
    },
  },
  enableThird: {
    type: Boolean,
    default: true,
  },
  enableEnterKey: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '请选择',
  },
});

const values = ref<number[]>([]);

const categories = ref([] as IBaseOpt[]);
const subCategories = ref([] as IBaseOpt[]);
const thirdCategories = ref([] as IBaseOpt[]);
const noThirdOpts = ref(false);

function onCateChange(id: number) {
  values.value[0] = id;
  values.value[1] = undefined;
  values.value[2] = undefined;
  subCategories.value = categories.value.find((v) => v.id === id)?.children || [];
  emit('change', [values.value[0], undefined, undefined]);
}

function onSubCateChange(id: number) {
  values.value[1] = id;
  values.value[2] = undefined;
  thirdCategories.value = subCategories.value.find((v) => v.id === id)?.children || [];

  noThirdOpts.value = thirdCategories.value?.length === 0;

  emit('without-third-category', noThirdOpts.value);
  emit('change', [values.value[0], values.value[1], undefined], noThirdOpts.value);
}

function onThirdCateChange(id: number) {
  values.value[2] = id;
  emit('change', values.value);
  emit('get-mcc', thirdCategories.value.find((v) => v.id === id)?.mccCode || '');
}

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  emit('key-enter');
}

async function initIndustryTree() {
  try {

    const data = await queryIndustryTree();

    categories.value = data as IBaseOpt[];

  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function initData() {
  if (!categories.value.length) {
    await initIndustryTree();
  }

  const codes = props.initialCodes;

  if (codes.length > 0) {
    if (codes[0] && codes[1]) {
      subCategories.value = categories.value.find((v) => v.id === codes[0])?.children || [];
    }

    if (codes[0] && codes[1] && codes[2]) {
      thirdCategories.value = subCategories.value.find((v) => v.id === codes[1])?.children || [];
      emit('get-mcc', thirdCategories.value.find((v) => v.id === codes[2])?.mccCode || '');
    }

    values.value = codes.map((v) => isNull(v) ? undefined : v);
  }
}

watch(
  () => props.initialCodes,
  () => {
    initData();
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.ins-select-box {
  display: flex;
  width: 100%;

  .el-select {
    flex: 1 1;
  }
}

:deep(.asb-ins),
:deep(.asb-ins-third) {
  margin-left: 9px;
}
</style>
